import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Department } from '@/types/supabase';
import { useActivityLogger } from '@/hooks/use-activity-logger';

interface OrganizationMember {
  id: string;
  user_id: string;
  email: string;
  role: string;
  access_level: string;
  department_id?: string;
  department_name?: string;
}

export const RoleManagement = () => {
  const { currentOrganization, isPrimaryFounder } = useAuth();
  const { logActivity } = useActivityLogger();
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isEditRoleOpen, setIsEditRoleOpen] = useState(false);
  const [currentMember, setCurrentMember] = useState<OrganizationMember | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    role: 'team_member',
    access_level: 'standard',
    department_id: '',
  });

  useEffect(() => {
    if (currentOrganization) {
      fetchMembers();
      fetchDepartments();
    }
  }, [currentOrganization]);

  const fetchMembers = async () => {
    if (!currentOrganization) return;

    try {
      setLoading(true);
      
      const { data: userOrgs, error: userOrgsError } = await supabase
        .from('user_organizations')
        .select('*')
        .eq('organization_id', currentOrganization.organization.id);

      if (userOrgsError) throw userOrgsError;
      
      const formattedMembers: OrganizationMember[] = [];
      
      for (const userOrg of userOrgs) {
        let departmentName = undefined;
        if (userOrg.department_id) {
          const { data: deptData, error: deptError } = await supabase
            .from('departments')
            .select('name')
            .eq('id', userOrg.department_id)
            .single();
            
          if (!deptError && deptData) {
            departmentName = deptData.name;
          }
        }
        
        formattedMembers.push({
          id: userOrg.id,
          user_id: userOrg.user_id,
          email: `User ${userOrg.user_id.substring(0, 8)}...`,
          role: userOrg.role,
          access_level: userOrg.access_level,
          department_id: userOrg.department_id,
          department_name: departmentName,
        });
      }
      
      setMembers(formattedMembers);
    } catch (error: any) {
      toast.error(`Error loading members: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    if (!currentOrganization) return;

    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('organization_id', currentOrganization.organization.id)
        .order('name');

      if (error) throw error;
      setDepartments(data as Department[]);
    } catch (error: any) {
      console.error('Error loading departments:', error.message);
    }
  };

  const handleInviteUser = async () => {
    if (!currentOrganization || !formData.email.trim()) return;

    try {
      toast.success(`Invitation would be sent to ${formData.email}`);
      
      await logActivity({
        action: 'create', 
        resourceType: 'user_organizations', 
        details: { email: formData.email, role: formData.role }
      });
      
      setIsInviteOpen(false);
      setFormData({
        email: '',
        role: 'team_member',
        access_level: 'standard',
        department_id: '',
      });
    } catch (error: any) {
      toast.error(`Error inviting user: ${error.message}`);
    }
  };

  const handleUpdateRole = async () => {
    if (!currentMember) return;
    
    try {
      const { error } = await supabase
        .from('user_organizations')
        .update({
          role: formData.role,
          access_level: formData.access_level,
          department_id: formData.department_id || null,
        })
        .eq('id', currentMember.id);

      if (error) throw error;
      
      setMembers(members.map(member => {
        if (member.id === currentMember.id) {
          const dept = departments.find(d => d.id === formData.department_id);
          return {
            ...member,
            role: formData.role,
            access_level: formData.access_level,
            department_id: formData.department_id || undefined,
            department_name: dept?.name,
          };
        }
        return member;
      }));
      
      await logActivity({
        action: 'update', 
        resourceType: 'user_organizations', 
        resourceId: currentMember.id,
        details: { 
          old: { 
            role: currentMember.role,
            access_level: currentMember.access_level,
            department_id: currentMember.department_id
          },
          new: {
            role: formData.role,
            access_level: formData.access_level,
            department_id: formData.department_id
          }
        }
      });
      
      setIsEditRoleOpen(false);
      setCurrentMember(null);
      toast.success('User role updated successfully');
    } catch (error: any) {
      toast.error(`Error updating role: ${error.message}`);
    }
  };

  const handleRemoveMember = async (id: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    try {
      const { error } = await supabase
        .from('user_organizations')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setMembers(members.filter(member => member.id !== id));
      
      await logActivity({
        action: 'delete', 
        resourceType: 'user_organizations', 
        resourceId: id
      });
      
      toast.success('Member removed successfully');
    } catch (error: any) {
      toast.error(`Error removing member: ${error.message}`);
    }
  };

  const openEditRoleDialog = (member: OrganizationMember) => {
    setCurrentMember(member);
    setFormData({
      email: member.email,
      role: member.role,
      access_level: member.access_level,
      department_id: member.department_id || '',
    });
    setIsEditRoleOpen(true);
  };

  const roleLabels: Record<string, string> = {
    'primary_founder': 'Primary Founder',
    'co_founder': 'Co-Founder',
    'department_head': 'Department Head',
    'team_member': 'Team Member',
  };

  if (!isPrimaryFounder()) {
    return (
      <div className="text-center p-8">
        <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground">Only the primary founder can manage team members and their roles.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Team Members</h2>
        <Button onClick={() => setIsInviteOpen(true)}>Invite Member</Button>
      </div>

      {loading ? (
        <div className="flex justify-center p-8">
          <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
        </div>
      ) : members.length === 0 ? (
        <div className="text-center p-8 border rounded-lg bg-background/50">
          <p className="text-muted-foreground">No team members found.</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Email</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Role</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Access Level</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Department</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {members.map((member) => (
                <tr key={member.id} className="bg-card">
                  <td className="px-4 py-3 text-sm">{member.email}</td>
                  <td className="px-4 py-3 text-sm">{roleLabels[member.role] || member.role}</td>
                  <td className="px-4 py-3 text-sm capitalize">{member.access_level}</td>
                  <td className="px-4 py-3 text-sm">{member.department_name || 'None'}</td>
                  <td className="px-4 py-3 text-sm text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEditRoleDialog(member)}>
                      Edit
                    </Button>
                    {member.role !== 'primary_founder' && (
                      <Button variant="ghost" size="sm" onClick={() => handleRemoveMember(member.id)}>
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>Send an invitation to join your organization</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="co_founder">Co-Founder</SelectItem>
                  <SelectItem value="department_head">Department Head</SelectItem>
                  <SelectItem value="team_member">Team Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="access_level">Access Level</Label>
              <Select
                value={formData.access_level}
                onValueChange={(value) => setFormData({ ...formData, access_level: value })}
              >
                <SelectTrigger id="access_level">
                  <SelectValue placeholder="Select access level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="elevated">Elevated</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Select
                value={formData.department_id}
                onValueChange={(value) => setFormData({ ...formData, department_id: value })}
              >
                <SelectTrigger id="department">
                  <SelectValue placeholder="Select a department (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsInviteOpen(false)}>Cancel</Button>
            <Button onClick={handleInviteUser} disabled={!formData.email.trim()}>Invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditRoleOpen} onOpenChange={setIsEditRoleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Member Role</DialogTitle>
            <DialogDescription>Update team member's role and permissions</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email Address</Label>
              <p className="text-sm text-muted-foreground">{currentMember?.email}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
                disabled={currentMember?.role === 'primary_founder'}
              >
                <SelectTrigger id="edit-role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {currentMember?.role === 'primary_founder' && (
                    <SelectItem value="primary_founder">Primary Founder</SelectItem>
                  )}
                  <SelectItem value="co_founder">Co-Founder</SelectItem>
                  <SelectItem value="department_head">Department Head</SelectItem>
                  <SelectItem value="team_member">Team Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-access_level">Access Level</Label>
              <Select
                value={formData.access_level}
                onValueChange={(value) => setFormData({ ...formData, access_level: value })}
              >
                <SelectTrigger id="edit-access_level">
                  <SelectValue placeholder="Select access level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="elevated">Elevated</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-department">Department</Label>
              <Select
                value={formData.department_id}
                onValueChange={(value) => setFormData({ ...formData, department_id: value })}
              >
                <SelectTrigger id="edit-department">
                  <SelectValue placeholder="Select a department (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">None</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditRoleOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateRole} disabled={currentMember?.role === 'primary_founder'}>Update</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

